/**
 * Large UPS Configuration Types (Frontend)
 * TypeScript interfaces and Zod schemas for the repeating Large UPS section (#996 Phase 4)
 *
 * IMPORTANT: Zod schemas must be wrapped in useMemo() for iOS to avoid crashes
 * @see GitLab #996 — Repeating Sections (Phase 4: Facility Power / Large UPS)
 *
 * Contract note: status/name use the GENERIC repeating-section column names
 * `status`/`name` (NOT `largeUpsStatus`/`largeUpsName`), mirroring the
 * Cooling Phase 2 types.
 *
 * The SHARED site-level Facility Power block (powerMainNominalLvVoltage,
 * powerFacilityDescription, powerSourceLocation, powerFireSuppressionNeeds, etc.)
 * and the DEFERRED Power Distribution fields are NOT part of this per-instance
 * model — they stay on the parent request and are rendered once at step level
 * (#996 Phase 4).
 */

import { z } from 'zod';

export type LargeUpsStatus = 'existing' | 'new';

/**
 * Large UPS Configuration interface (camelCase — matches API response)
 */
export interface LargeUpsConfiguration {
  id?: number;
  requestId: number;

  // Existing vs New status — generic repeating-section field (#996)
  status: LargeUpsStatus;

  // User-defined instance name (auto-named "Large UPS N" server-side when omitted)
  name?: string;

  locationTagId?: number | null;

  // Per-instance Large UPS fields (7) — keys mirror the live UI dataField keys
  powerExistingSiteProtectedByLargeKwUps?: string;
  powerLargeUpsDescription?: string;
  powerModelOfExistingUps?: string;
  powerLevelOfRedundancy?: string;
  powerRequiredRedundancyForUps?: string;
  powerBatteryChemistry?: string;
  powerBatteryRuntimeRequiredMin?: string;

  // #997 QA-A2: per-instance site-power fields (formerly request-level shared, migration 111)
  powerSourceLocation?: string;
  powerFireSuppressionNeeds?: string;

  // #997 Bug 2: per-instance Additional Information note (formerly request-level shared)
  powerAdditionalInformation?: string;

  // Metadata
  displayOrder: number;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Input for creating a new large UPS configuration
 */
export type CreateLargeUpsConfigurationInput = Omit<
  LargeUpsConfiguration,
  'id' | 'createdAt' | 'updatedAt'
>;

/**
 * Input for updating a large UPS configuration
 */
export type UpdateLargeUpsConfigurationInput = Partial<
  Omit<LargeUpsConfiguration, 'id' | 'requestId' | 'createdAt' | 'updatedAt'>
>;

/**
 * Grouped large UPS configurations for the segmented view (existing vs new)
 */
export interface GroupedLargeUpsConfigurations {
  existing: LargeUpsConfiguration[];
  new: LargeUpsConfiguration[];
}

/**
 * API response for large UPS configurations list
 */
export interface LargeUpsConfigurationsResponse {
  largeUps: LargeUpsConfiguration[];
  total: number;
}

/**
 * Zod schema factory for large UPS configuration validation
 * IMPORTANT: Must be wrapped in useMemo() to avoid iOS crashes
 *
 * @example
 * const schema = useMemo(() => createLargeUpsConfigurationSchema(), []);
 */
export const createLargeUpsConfigurationSchema = () =>
  z.object({
    id: z.number().optional(),
    requestId: z.number(),
    status: z.enum(['existing', 'new']),

    // User-defined instance name
    name: z.string().optional(),

    locationTagId: z.number().int().nullable().optional(),

    // Per-instance fields
    powerExistingSiteProtectedByLargeKwUps: z.string().optional(),
    powerLargeUpsDescription: z.string().optional(),
    powerModelOfExistingUps: z.string().optional(),
    powerLevelOfRedundancy: z.string().optional(),
    powerRequiredRedundancyForUps: z.string().optional(),
    powerBatteryChemistry: z.string().optional(),
    powerBatteryRuntimeRequiredMin: z.string().optional(),
    powerSourceLocation: z.string().optional(),
    powerFireSuppressionNeeds: z.string().optional(),
    powerAdditionalInformation: z.string().optional(),

    // Metadata
    displayOrder: z.number().min(0),
    createdAt: z.string().optional(),
    updatedAt: z.string().optional(),
  });

/**
 * Zod schema factory for create large UPS input validation
 * IMPORTANT: Must be wrapped in useMemo() to avoid iOS crashes
 */
export const createLargeUpsInputSchema = () =>
  createLargeUpsConfigurationSchema().omit({
    id: true,
    createdAt: true,
    updatedAt: true,
  });

/**
 * Zod schema factory for update large UPS input validation
 * IMPORTANT: Must be wrapped in useMemo() to avoid iOS crashes
 */
export const updateLargeUpsInputSchema = () =>
  createLargeUpsConfigurationSchema()
    .omit({
      id: true,
      requestId: true,
      createdAt: true,
      updatedAt: true,
    })
    .partial();

/**
 * Type inference from Zod schemas
 */
export type LargeUpsConfigurationSchema = z.infer<
  ReturnType<typeof createLargeUpsConfigurationSchema>
>;
export type CreateLargeUpsInputSchema = z.infer<
  ReturnType<typeof createLargeUpsInputSchema>
>;
export type UpdateLargeUpsInputSchema = z.infer<
  ReturnType<typeof updateLargeUpsInputSchema>
>;

/** Status options for large UPS instances. */
export const LARGE_UPS_STATUS_OPTIONS: LargeUpsStatus[] = ['existing', 'new'];

/**
 * Default values for a new large UPS configuration
 */
export const DEFAULT_LARGE_UPS_CONFIGURATION: Partial<LargeUpsConfiguration> = {
  status: 'new',
  displayOrder: 0,
};
